import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Hospitalisation v2 API client (api-hospitalisation-v2-service). Same shape
 * as the earlier v2 clients (CC / Dx / Med / Add / Fam / Act / Alg).
 *
 * Contract notes:
 *
 * 1. Every mutating call needs `expectedVersion` (the last version the caller
 *    saw). On mismatch the server returns 409 with a fresh doc.
 *
 * 2. `cause` is a snapshot from api-icd-service pick: `{icdCode, icdName,
 *    description?}`. Frontend calls the ICD service directly via
 *    IcdAutocomplete (chiefComplaints/), then snapshots the pick into the
 *    body here.
 *
 * 3. `procedures` is a snapshot list from the federated /procedure-catalog:
 *    `[{code, name, source}]` where source ∈ "catalog" | "free-text".
 *
 * 4. `status` is 2-state ("hospitalised" | "discharged"). Backend enforces
 *    `dischargeDate` presence when status=discharged and rejects a discharge
 *    date earlier than the hospitalisation date.
 *
 * 5. Delete is soft — server flips deleted=true; the row disappears from
 *    subsequent list responses.
 */
class HospitalisationService {
  constructor() {
    this.apiService = new APIService();
  }

  list(patientId, onResponse, onError) {
    const url = APIConfig.hospitalisationV2.list.format(patientId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Add a hospitalisation. Body shape matches beans.AddRequest:
   *   { cause: {icdCode, icdName, description?}, status,
   *     hospitalisationDate, dischargeDate?, procedures: [{code, name, source}],
   *     description?, expectedVersion }
   */
  add(patientId, body, onResponse, onError) {
    const url = APIConfig.hospitalisationV2.add.format(patientId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Full-detail edit. Same body shape as add; server preserves AddedBy/AddedAt.
   */
  update(patientId, hospitalisationId, body, onResponse, onError) {
    const url = APIConfig.hospitalisationV2.update.format(patientId, hospitalisationId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Soft-delete. Body carries `expectedVersion`. APIService has no DELETE
   * helper — direct fetch like the other v2 clients.
   */
  delete(patientId, hospitalisationId, expectedVersion, onResponse, onError) {
    const url = APIConfig.hospitalisationV2.delete.format(patientId, hospitalisationId);
    return fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        client: this._client(),
        phone_no: this._header("phone_no"),
        auth_token: this._header("auth_token"),
      },
      body: JSON.stringify({ expectedVersion: expectedVersion || 0 }),
    })
      .then((r) => r.json().then((data) => (r.ok ? onResponse(data) : onError(data))))
      .catch((err) =>
        onError({ message: err && err.message ? err.message : "Delete failed" })
      );
  }

  _header(name) {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      if (name === "phone_no") return (user && user.phoneno) || "";
      if (name === "auth_token") {
        return (user && user.accessToken && user.accessToken.accessToken) || "";
      }
    } catch (e) {
      /* localStorage unavailable — return empty and let the server 401 */
    }
    return "";
  }

  _client() {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      const isDoctor =
        user && user.doctorProfile && user.doctorProfile.id > 0 &&
        !(user.employeeProfile && user.employeeProfile.id > 0);
      return isDoctor ? "doctor_web" : "bridge_web";
    } catch (e) {
      return "bridge_web";
    }
  }
}

export default HospitalisationService;
